import React, { useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Button,
  Icon,
  BlockHead,
  BlockBetween,
  BlockTitle,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  TooltipComponent,
  PaginationComponent,
} from "../../../../components/Component";
import { getPublicUrl } from "../../../../utils/Utils";
import PartnerStatusBadge from "../../../../components/features/medical-partner-admin/PartnerStatusBadge";
import ClinicPricesShortcutModal from "./ClinicPricesShortcutModal";
import PlatformListingPanel from "../../../../components/features/platform-listing/PlatformListingPanel";

function isMedicalTourismOn(clinic) {
  return clinic?.offersMedicalTourism !== false;
}

/**
 * Medical partner dossier — Clinics tab: list, TC/MT offer toggles, shortcuts and price entry modal.
 */
const PartnerDetailsClinicsTab = ({
  partner,
  clinics,
  clinicPage,
  clinicLimit,
  totalClinics,
  hasPrimaryUser,
  clinicChannelBusyId,
  onClinicOffersPatch,
  onConfirmUpdateClinic,
  onOpenClinicDetails,
  onFetchClinicsPage,
}) => {
  const history = useHistory();
  const [pricesModalClinic, setPricesModalClinic] = useState(null);

  const openPricesModal = useCallback((clinic) => {
    setPricesModalClinic(clinic);
  }, []);

  const closePricesModal = useCallback(() => {
    setPricesModalClinic(null);
  }, []);

  const goToFullPriceEditor = useCallback(() => {
    const id = pricesModalClinic?._id;
    if (!id) return;
    closePricesModal();
    history.push(getPublicUrl(`clinics/${id}/prices`));
  }, [pricesModalClinic, closePricesModal, history]);

  if (!partner?._id) return null;

  return (
    <div>
      <BlockHead size="sm" className="mb-4">
        <BlockBetween>
          <BlockTitle tag="h5">Clinic list</BlockTitle>
          <Button
            color="primary"
            size="sm"
            onClick={() => window.open(getPublicUrl(`clinics?partnerId=${partner._id}`), "_blank")}
          >
            <Icon name="plus" />
            <span>Add clinic</span>
          </Button>
        </BlockBetween>
      </BlockHead>

      {clinics.length === 0 ? (
        <div className="text-center p-5">
          <p className="text-soft">No clinic found</p>
        </div>
      ) : (
        <DataTable className="card-stretch" scrollable>
          <DataTableBody>
            <DataTableHead>
              <DataTableRow>
                <span className="sub-text">Name</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Address</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Status</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Default</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text d-flex flex-column">
                  <span>Teleconsultation offer</span>
                  <span className="small text-soft font-weight-normal">On / off</span>
                </span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text d-flex flex-column">
                  <span>Medical Tourism offer</span>
                  <span className="small text-soft font-weight-normal">On / off</span>
                </span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Created</span>
              </DataTableRow>
              <DataTableRow className="nk-tb-col-tools text-right"></DataTableRow>
            </DataTableHead>
            {clinics.map((clinic) => {
              const cid = clinic?._id ? String(clinic._id) : "";
              const rowBusy = Boolean(clinicChannelBusyId && cid && String(clinicChannelBusyId) === cid);
              const offersTc = !!clinic.offersTeleconsultation;
              const offersMt = isMedicalTourismOn(clinic);
              const togglesDisabled = !hasPrimaryUser || !cid || rowBusy;

              return (
                <DataTableItem key={clinic._id}>
                  <DataTableRow dataLabel="Name">
                    <span className="tb-lead">
                      {clinic.name || clinic.clinicName || "N/A"}
                      {clinic.isDefault && (
                        <Badge className="badge-dim badge-primary ms-2">Default</Badge>
                      )}
                    </span>
                  </DataTableRow>
                  <DataTableRow dataLabel="Address">
                    <span>
                      {clinic.address && typeof clinic.address === "object" ? (
                        <>
                          {clinic.address.street || clinic.address.address || ""}
                          {clinic.address.street && clinic.address.city ? ", " : ""}
                          {clinic.address.city || ""}
                          {clinic.address.city && clinic.address.country ? ", " : ""}
                          {clinic.address.country || ""}
                          {!clinic.address.street && !clinic.address.city && !clinic.address.country ? "N/A" : ""}
                        </>
                      ) : (
                        clinic.address || "N/A"
                      )}
                    </span>
                  </DataTableRow>
                  <DataTableRow dataLabel="Status">
                    <PartnerStatusBadge status={clinic.status} labelMode="listing" />
                  </DataTableRow>
                  <DataTableRow dataLabel="Default">
                    <span>{clinic.isDefault ? "Yes" : "No"}</span>
                  </DataTableRow>
                  <DataTableRow dataLabel="TC offer">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id={`clinic-list-tc-${cid || "x"}`}
                        checked={offersTc}
                        disabled={togglesDisabled}
                        onChange={(e) => {
                          if (!cid || togglesDisabled) return;
                          void onClinicOffersPatch(cid, { offersTeleconsultation: e.target.checked });
                        }}
                      />
                      <label className="custom-control-label sr-only" htmlFor={`clinic-list-tc-${cid || "x"}`}>
                        Offers teleconsultation
                      </label>
                    </div>
                  </DataTableRow>
                  <DataTableRow dataLabel="MT offer">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id={`clinic-list-mt-${cid || "x"}`}
                        checked={offersMt}
                        disabled={togglesDisabled}
                        onChange={(e) => {
                          if (!cid || togglesDisabled) return;
                          void onClinicOffersPatch(cid, { offersMedicalTourism: e.target.checked });
                        }}
                      />
                      <label className="custom-control-label sr-only" htmlFor={`clinic-list-mt-${cid || "x"}`}>
                        Offers medical tourism
                      </label>
                    </div>
                  </DataTableRow>
                  <DataTableRow dataLabel="Created">
                    <span>{clinic.createdAt ? moment(clinic.createdAt).format("DD/MM/YYYY") : "N/A"}</span>
                  </DataTableRow>
                  <DataTableRow className="nk-tb-col-tools" dataLabel="Actions">
                    <ul className="nk-tb-actions gx-1">
                      <li>
                        <TooltipComponent
                          tag="a"
                          href="#view-clinic"
                          containerClassName="btn btn-trigger btn-icon"
                          id={`clinic-view-${cid}`}
                          icon="eye"
                          direction="top"
                          text="View details"
                          onClick={(e) => {
                            e.preventDefault();
                            onOpenClinicDetails(clinic);
                          }}
                        />
                      </li>
                      <li>
                        <TooltipComponent
                          tag="a"
                          href="#clinic-prices"
                          containerClassName="btn btn-trigger btn-icon"
                          id={`clinic-prices-${cid}`}
                          icon="calc"
                          direction="top"
                          text="Prices (modal)"
                          onClick={(e) => {
                            e.preventDefault();
                            openPricesModal(clinic);
                          }}
                        />
                      </li>
                      {(clinic.status === "pending" || clinic.status === "Pending") && (
                        <>
                          <li>
                            <TooltipComponent
                              tag="a"
                              containerClassName="btn btn-trigger btn-icon text-success"
                              id={`approve-clinic-${cid}`}
                              icon="check-fill"
                              direction="top"
                              text="Approve"
                              onClick={(e) => {
                                e.preventDefault();
                                onConfirmUpdateClinic(clinic, "active");
                              }}
                            />
                          </li>
                          <li>
                            <TooltipComponent
                              tag="a"
                              containerClassName="btn btn-trigger btn-icon text-danger"
                              id={`reject-clinic-${cid}`}
                              icon="cross-fill"
                              direction="top"
                              text="Reject"
                              onClick={(e) => {
                                e.preventDefault();
                                onConfirmUpdateClinic(clinic, "inactive");
                              }}
                            />
                          </li>
                        </>
                      )}
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                            <Icon name="more-h"></Icon>
                          </DropdownToggle>
                          <DropdownMenu right>
                            <ul className="link-list-opt no-bdr">
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#view"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    onOpenClinicDetails(clinic);
                                  }}
                                >
                                  <Icon name="eye"></Icon>
                                  <span>View details</span>
                                </DropdownItem>
                              </li>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#prices-modal"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openPricesModal(clinic);
                                  }}
                                >
                                  <Icon name="calc"></Icon>
                                  <span>Edit prices…</span>
                                </DropdownItem>
                              </li>
                              {(clinic.status === "pending" || clinic.status === "Pending") && (
                                <>
                                  <li>
                                    <DropdownItem
                                      tag="a"
                                      href="#approve"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        onConfirmUpdateClinic(clinic, "active");
                                      }}
                                    >
                                      <Icon name="check-fill"></Icon>
                                      <span>Approve clinic</span>
                                    </DropdownItem>
                                  </li>
                                  <li>
                                    <DropdownItem
                                      tag="a"
                                      href="#reject"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        onConfirmUpdateClinic(clinic, "inactive");
                                      }}
                                    >
                                      <Icon name="cross-fill"></Icon>
                                      <span>Reject clinic</span>
                                    </DropdownItem>
                                  </li>
                                </>
                              )}
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#clinic-mgmt"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    history.push(
                                      getPublicUrl(`clinics?partnerId=${partner._id}&openClinic=${clinic._id}`)
                                    );
                                  }}
                                >
                                  <Icon name="home"></Icon>
                                  <span>Open in Clinic management</span>
                                </DropdownItem>
                              </li>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#prices"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    history.push(getPublicUrl(`clinics/${clinic._id}/prices`));
                                  }}
                                >
                                  <Icon name="calc"></Icon>
                                  <span>Manage prices (full page)</span>
                                </DropdownItem>
                              </li>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#calendar"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    history.push(getPublicUrl(`clinics/${clinic._id}/calendar`));
                                  }}
                                >
                                  <Icon name="calendar"></Icon>
                                  <span>TC calendar / slots</span>
                                </DropdownItem>
                              </li>
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </li>
                    </ul>
                  </DataTableRow>
                </DataTableItem>
              );
            })}
          </DataTableBody>
        </DataTable>
      )}

      {totalClinics > clinicLimit && (
        <div className="mt-3 d-flex justify-content-between align-items-center flex-wrap">
          <span className="text-soft">
            Page {clinicPage} of {Math.ceil(totalClinics / clinicLimit)}
          </span>
          <PaginationComponent
            itemPerPage={clinicLimit}
            totalItems={totalClinics}
            currentPage={clinicPage}
            paginate={(p) => onFetchClinicsPage(p)}
          />
        </div>
      )}

      {clinics.some((c) => c.offersTeleconsultation) ? (
        <>
          <BlockHead size="sm" className="mt-4">
            <BlockTitle tag="h5">Teleconsultation listing status</BlockTitle>
          </BlockHead>
          <p className="text-soft small mb-3">
            Read-only status. Approve or reject catalog listing in Catalogue go-live.
          </p>
          {clinics
            .filter((c) => c.offersTeleconsultation)
            .map((c) => (
              <PlatformListingPanel
                key={c._id}
                clinicId={c._id}
                title={`${c.name || c.clinicName || "Clinic"} — platform listing`}
                compact
              />
            ))}
        </>
      ) : null}

      <ClinicPricesShortcutModal
        isOpen={Boolean(pricesModalClinic)}
        clinic={pricesModalClinic}
        onClose={closePricesModal}
        onOpenFullEditor={goToFullPriceEditor}
      />
    </div>
  );
};

export default PartnerDetailsClinicsTab;
